import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowCircleLeft } from "@fortawesome/pro-regular-svg-icons/faArrowCircleLeft";
import { faCloudDownloadAlt } from "@fortawesome/pro-regular-svg-icons/faCloudDownloadAlt";

import { Helmet } from "react-helmet";
import axios from "axios";
import dayjs from "dayjs";
import download from "downloadjs";
import { toast } from "react-toastify";
import { Heading, Group, Form, Select, DateRange } from "@peracto/peracto-ui";
import { GET_MANY, useClient } from "@peracto/client";
import { useConfig } from "@peracto/peracto-config";

const WarrantyExport = () => {
    const config = useConfig();
    const API_URL = config.get("api");

    const { client } = useClient();

    const [countries, setCountries] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchCountries = async () => {
        try {
            const { data: countryData } = await client(GET_MANY, "countries", {
                id: "countries",
            });

            if (countryData?.length > 0 && Array.isArray(countryData)) {
                setCountries(countryData);
            }

            setLoading(false);
        } catch (e) {
            console.error(e);
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCountries();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Prevent enter key submitting form
    function onKeyDown(keyEvent) {
        if ((keyEvent.charCode || keyEvent.keyCode) === 13) {
            keyEvent.preventDefault();
        }
    }

    const handleSubmit = async (values) => {
        const params = new URLSearchParams();

        if (values.countryCode) {
            params.append("countryCode", values.countryCode);
        }

        if (values.createdAtStart && values.createdAtEnd) {
            params.append("createdAt[operator]", "between");
            params.append(
                "createdAt[value][start]",
                dayjs(values.createdAtStart).format("YYYY-MM-DD HH:mm:ss"),
            );
            params.append(
                "createdAt[value][end]",
                dayjs(values.createdAtEnd).format("YYYY-MM-DD HH:mm:ss"),
            );
        }

        const query = params.toString();

        try {
            const { data } = await axios.get(
                `${API_URL}/export/warranty_registration.csv${query ? `?${query}` : ""}`,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("token")}`,
                    },
                },
            );

            const filenameParts = [];
            if (values.countryCode) filenameParts.push(values.countryCode);
            if (values.createdAtStart && values.createdAtEnd) {
                filenameParts.push(
                    dayjs(values.createdAtStart).format("YYYY-MM-DD"),
                    "to",
                    dayjs(values.createdAtEnd).format("YYYY-MM-DD"),
                );
            }

            download(
                data,
                `warranty_registration_export${
                    filenameParts.length ? `_${filenameParts.join("_")}` : ""
                }.csv`,
            );
        } catch (e) {
            console.error(e);
            toast.error(
                e?.error?.body?.hasOwnProperty("hydra:description")
                    ? e.error.body["hydra:description"]
                    : "Whoops, there was a problem exporting the warranty registrations...",
            );
        }
    };

    return (
        <>
            <Helmet>
                <title>Export Warranty Registrations | Peracto</title>
            </Helmet>
            <div className="form-container">
                <Heading name="Export Warranty Registrations">
                    <div className="d-flex align-items-center ml-2">
                        <Link
                            className="btn btn-outline-primary"
                            to="/warranty-registrations"
                        >
                            <FontAwesomeIcon
                                icon={faArrowCircleLeft}
                                className="mr-2"
                            />
                            Back to Warranty
                        </Link>
                    </div>
                </Heading>

                <div className="row">
                    <div className="col-12">
                        <Group
                            key="warranty-registrations"
                            id="warranty-registrations"
                            name="Warranty Registrations"
                        >
                            <Form
                                values={{
                                    countryCode: "",
                                    createdAtStart: "",
                                    createdAtEnd: "",
                                }}
                                showActionPanel={false}
                                onSubmit={(values) => handleSubmit(values)}
                                onKeyDown={onKeyDown}
                            >
                                <div className="row">
                                    <div className="col-12">
                                        <Select
                                            name="countryCode"
                                            label="Country"
                                            options={[
                                                { label: "All", value: "" },
                                                ...countries.map((country) => ({
                                                    label: `${country.label} (${country.iso3})`,
                                                    value: country.iso3,
                                                })),
                                            ]}
                                        />

                                        <DateRange
                                            fromName="createdAtStart"
                                            toName="createdAtEnd"
                                            fromLabel="Created At (from) "
                                            toLabel="Created At (to)"
                                        />

                                        <button
                                            className="btn btn-outline-primary"
                                            type="submit"
                                            data-testid="export-warranty-registrations"
                                            disabled={loading}
                                        >
                                            <FontAwesomeIcon
                                                icon={faCloudDownloadAlt}
                                                className="mr-2"
                                            />
                                            Export Warranty Registrations
                                        </button>
                                    </div>
                                </div>
                            </Form>
                        </Group>
                    </div>
                </div>
            </div>
        </>
    );
};

export default WarrantyExport;
